const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const SESSION_TOKEN_KEY = "spike_auth_token";
const QUEUE_STORAGE_KEY = "spike_zone_outcome_persistence_queue";
const STATUS_CACHE_STORAGE_KEY = "spike_zone_outcome_persistence_status";
const DEFAULT_RETRY_INTERVAL_MS = 5000;
const DEFAULT_MAX_RETRIES = 5;
const DEFAULT_BACKOFF_MULTIPLIER = 2;
const MAX_STATUS_CACHE_SIZE = 300;

function canUseBrowserApis() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function safeParseJson(value, fallback) {
  if (!value) {
    return fallback;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
}

function normalizeTimestamp(value) {
  if (value == null || value === "") {
    return null;
  }

  const numericValue = Number(value);

  if (Number.isFinite(numericValue)) {
    return numericValue;
  }

  const parsedValue = Date.parse(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

function toFiniteNumber(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function createStatusSnapshot(entry) {
  return {
    id: entry.id,
    persistenceStatus: entry.status,
    persisted: entry.status === "saved",
    retryCount: entry.retryCount || 0,
    lastError: entry.lastError || null,
    updatedAt: entry.updatedAt || Date.now(),
  };
}

function createQueueEntry(result, context = {}) {
  const id = String(result?.tradeKey || result?.id || "");

  if (!id) {
    return null;
  }

  const symbol = context.symbol || result.symbol || result.pair || null;
  const timeframe = context.timeframe ?? result.timeframe ?? null;

  return {
    id,
    payload: {
      tradeKey: id,
      symbol,
      timeframe,
      market: result.market ?? null,
      direction: result.direction ?? null,
      bosPrice: result.bosPrice ?? null,
      bosIndex: result.bosIndex ?? null,
      bosTime: result.bosTime ?? null,
      zoneId: result.zoneId ?? result.signal?.zoneId ?? null,
      zoneType: result.zoneType ?? result.signal?.zoneType ?? null,
      zoneHigh: result.zoneHigh ?? result.zoneRange?.high ?? null,
      zoneLow: result.zoneLow ?? result.zoneRange?.low ?? null,
      entryPrice: result.entryPrice ?? null,
      stopLoss: result.stopLoss ?? null,
      takeProfit: result.takeProfit ?? null,
      result: result.result ?? null,
      signalTimestamp: normalizeTimestamp(
        result.signalTimestamp ?? result.signal?.timestamp ?? result.createdAt ?? result.entryTime
      ),
      evaluatedAt: normalizeTimestamp(result.evaluatedAt ?? result.resultTime ?? result.updatedAt),
    },
    status: result.persistenceStatus === "saved" ? "saved" : "pending",
    retryCount: 0,
    nextAttemptAt: Date.now(),
    lastError: null,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

class ZoneOutcomePersistenceService {
  constructor(config = {}) {
    this.retryIntervalMs = config.retryIntervalMs || DEFAULT_RETRY_INTERVAL_MS;
    this.maxRetries = config.maxRetries || DEFAULT_MAX_RETRIES;
    this.backoffMultiplier = config.backoffMultiplier || DEFAULT_BACKOFF_MULTIPLIER;
    this.queue = new Map();
    this.statusCache = new Map();
    this.listeners = new Set();
    this.processing = false;
    this.intervalId = null;
    this.started = false;
    this.boundHandleStorage = this.handleStorage.bind(this);
    this.boundHandleWakeUp = this.handleWakeUp.bind(this);
    this.hydrate();
    this.start();
  }

  hydrate() {
    if (!canUseBrowserApis()) {
      return;
    }

    this.queue.clear();
    this.statusCache.clear();

    const storedQueue = safeParseJson(
      window.localStorage.getItem(QUEUE_STORAGE_KEY),
      []
    );
    const storedStatuses = safeParseJson(
      window.localStorage.getItem(STATUS_CACHE_STORAGE_KEY),
      []
    );

    storedQueue.forEach((entry) => {
      if (!entry?.id || !entry?.payload?.tradeKey) {
        return;
      }

      this.queue.set(entry.id, {
        ...entry,
        status: entry.status === "saved" ? "saved" : entry.status || "pending",
        retryCount: Number(entry.retryCount) || 0,
        nextAttemptAt: Number(entry.nextAttemptAt) || Date.now(),
        updatedAt: Number(entry.updatedAt) || Date.now(),
      });
    });

    storedStatuses.forEach((entry) => {
      if (!entry?.id || !entry?.status) {
        return;
      }

      this.statusCache.set(entry.id, {
        status: entry.status,
        updatedAt: Number(entry.updatedAt) || Date.now(),
      });
    });
  }

  start() {
    if (!canUseBrowserApis() || this.started) {
      return;
    }

    this.started = true;
    this.intervalId = window.setInterval(() => {
      void this.processQueue();
    }, this.retryIntervalMs);

    window.addEventListener("storage", this.boundHandleStorage);
    window.addEventListener("online", this.boundHandleWakeUp);
    window.addEventListener("focus", this.boundHandleWakeUp);

    void this.processQueue();
  }

  subscribe(listener) {
    if (typeof listener !== "function") {
      return () => {};
    }

    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  enqueue(result, context = {}) {
    const entry = createQueueEntry(result, context);

    if (!entry) {
      return null;
    }

    const savedStatus = this.statusCache.get(entry.id);

    if (savedStatus?.status === "saved") {
      this.emit({
        ...createStatusSnapshot({
          ...entry,
          status: "saved",
          updatedAt: savedStatus.updatedAt,
        }),
      });
      return entry.id;
    }

    const existingEntry = this.queue.get(entry.id);

    if (existingEntry?.status === "saved") {
      this.emit(createStatusSnapshot(existingEntry));
      return entry.id;
    }

    const nextEntry = existingEntry
      ? {
          ...existingEntry,
          payload: {
            ...existingEntry.payload,
            ...entry.payload,
          },
          updatedAt: Date.now(),
        }
      : entry;

    if (nextEntry.status !== "sending") {
      nextEntry.status = "pending";
      nextEntry.nextAttemptAt = Date.now();
    }

    this.queue.set(entry.id, nextEntry);
    this.persistQueue();
    this.emit(createStatusSnapshot(nextEntry));
    void this.processQueue();
    return entry.id;
  }

  handleStorage(event) {
    if (event.key === SESSION_TOKEN_KEY || event.key === QUEUE_STORAGE_KEY) {
      this.hydrate();
      void this.processQueue();
    }
  }

  handleWakeUp() {
    void this.processQueue();
  }

  getToken() {
    if (!canUseBrowserApis()) {
      return "";
    }

    return window.localStorage.getItem(SESSION_TOKEN_KEY) || "";
  }

  async processQueue() {
    if (this.processing) {
      return;
    }

    const token = this.getToken();

    if (!token) {
      this.persistQueue();
      return;
    }

    const now = Date.now();
    const pendingEntries = Array.from(this.queue.values())
      .filter((entry) => {
        if (!entry?.id || entry.status === "saved" || entry.status === "sending") {
          return false;
        }

        if ((entry.retryCount || 0) >= this.maxRetries && entry.status === "failed") {
          return false;
        }

        return (entry.nextAttemptAt || 0) <= now;
      })
      .sort((left, right) => (left.nextAttemptAt || 0) - (right.nextAttemptAt || 0));

    if (!pendingEntries.length) {
      return;
    }

    this.processing = true;

    try {
      for (const entry of pendingEntries) {
        await this.sendEntry(entry, token);
      }
    } finally {
      this.processing = false;
    }
  }

  async sendEntry(entry, token) {
    const currentEntry = this.queue.get(entry.id);

    if (!currentEntry || currentEntry.status === "saved") {
      return;
    }

    const sendingEntry = {
      ...currentEntry,
      status: "sending",
      updatedAt: Date.now(),
      lastError: null,
    };
    this.queue.set(entry.id, sendingEntry);
    this.persistQueue();
    this.emit(createStatusSnapshot(sendingEntry));

    try {
      const response = await fetch(`${API_BASE_URL}/api/trade-results`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(sendingEntry.payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        const authError = response.status === 401 || response.status === 403;
        const nextError = new Error(errorText || "Failed to persist trade result");
        nextError.isAuthError = authError;
        throw nextError;
      }

      const savedEntry = {
        ...sendingEntry,
        status: "saved",
        updatedAt: Date.now(),
        nextAttemptAt: null,
        lastError: null,
      };
      this.queue.delete(entry.id);
      this.recordStatus(entry.id, "saved", savedEntry.updatedAt);
      this.persistQueue();
      this.emit(createStatusSnapshot(savedEntry));
    } catch (error) {
      const retryCount = error?.isAuthError
        ? sendingEntry.retryCount || 0
        : (sendingEntry.retryCount || 0) + 1;
      const maxedOut = retryCount >= this.maxRetries;
      const delayMs = this.retryIntervalMs * Math.max(1, this.backoffMultiplier ** (retryCount - 1));
      const failedEntry = {
        ...sendingEntry,
        status: "failed",
        retryCount,
        updatedAt: Date.now(),
        nextAttemptAt: Date.now() + (error?.isAuthError ? this.retryIntervalMs : delayMs),
        lastError: error.message || "Failed to persist trade result",
      };

      this.queue.set(entry.id, failedEntry);
      this.recordStatus(entry.id, "failed", failedEntry.updatedAt);
      this.persistQueue();
      this.emit(createStatusSnapshot(failedEntry));

      if (maxedOut) {
        return;
      }
    }
  }

  recordStatus(id, status, updatedAt) {
    this.statusCache.set(id, {
      status,
      updatedAt,
    });

    if (this.statusCache.size > MAX_STATUS_CACHE_SIZE) {
      const oldestKey = this.statusCache.keys().next().value;

      if (oldestKey) {
        this.statusCache.delete(oldestKey);
      }
    }

    this.persistStatusCache();
  }

  emit(snapshot) {
    this.listeners.forEach((listener) => {
      listener(snapshot);
    });
  }

  persistQueue() {
    if (!canUseBrowserApis()) {
      return;
    }

    window.localStorage.setItem(
      QUEUE_STORAGE_KEY,
      JSON.stringify(Array.from(this.queue.values()))
    );
  }

  persistStatusCache() {
    if (!canUseBrowserApis()) {
      return;
    }

    const serializedStatuses = Array.from(this.statusCache.entries()).map(([id, value]) => ({
      id,
      status: value.status,
      updatedAt: value.updatedAt,
    }));

    window.localStorage.setItem(
      STATUS_CACHE_STORAGE_KEY,
      JSON.stringify(serializedStatuses)
    );
  }
}

const zoneOutcomePersistenceService = new ZoneOutcomePersistenceService();

export function buildPersistencePatch(snapshot) {
  if (!snapshot?.id) {
    return null;
  }

  return {
    persisted: snapshot.persisted === true,
    persistenceStatus: snapshot.persistenceStatus,
    retryCount: toFiniteNumber(snapshot.retryCount) ?? 0,
    persistenceError: snapshot.lastError || null,
    persistedAt: snapshot.persistenceStatus === "saved" ? snapshot.updatedAt : null,
  };
}

export default zoneOutcomePersistenceService;
